import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Lottie from "react-lottie";
import * as animationData from "../../../assets/animation/floatingIcon.json";
import Section from "../../../components/atoms/Section";
import useWindowSize from "../../../hooks/useWindowSize";
import { func } from "../../../utils";
import {
  usePageContextFunction,
  usePageContextState,
} from "../../../context/PageContext";
import { Button, Loading, NewFooter } from "../../../components";
import { useFetchDrawingSoc } from "../../../networks";
import { FloatingIconWrapper } from "../HomeDetails/HomeDetails.styled";
const imgPath = `${process.env.REACT_APP_API_HOST}/uploads/featured-images/`;
// Trophy SVG Component
const TrophySVG = ({ className = "w-[42px] h-[84px]" }) => (
  <svg
    className={className}
    viewBox="0 0 42 84"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <linearGradient id="trophyGradient" x1="50%" y1="0%" x2="50%" y2="100%">
        <stop offset="0%" stopColor="#C27337" />
        <stop offset="96.875%" stopColor="#F4B919" />
      </linearGradient>
    </defs>
    <path
      d="M24.61 52.78L34.05 67.06V52.78H24.61Z"
      fill="url(#trophyGradient)"
    />
    <path
      d="M16.92 47.49L24.43 61.63V47.49H16.92Z"
      fill="url(#trophyGradient)"
    />
    <path d="M9.94 41.12L17.45 55.69V41.12H9.94Z" fill="url(#trophyGradient)" />
    <path d="M4.7 33.36L9.32 47.83V33.36H4.7Z" fill="url(#trophyGradient)" />
    <path d="M1.59 24.57L6.99 37.86V24.57H1.59Z" fill="url(#trophyGradient)" />
    <path d="M0 15.98L8.3 26.67V15.98H0Z" fill="url(#trophyGradient)" />
    <path d="M1.3 7.9L12.19 15.66V7.9H1.3Z" fill="url(#trophyGradient)" />
    <path d="M5.88 0L18.08 6.02V0H5.88Z" fill="url(#trophyGradient)" />
    <path d="M20.93 0L35.79 10.41V0H20.93Z" fill="url(#trophyGradient)" />
    <path
      d="M20.25 77.51L36.67 83.25V77.51H20.25Z"
      fill="url(#trophyGradient)"
    />
    <path
      d="M10.93 70.87L27.27 77.27V70.87H10.93Z"
      fill="url(#trophyGradient)"
    />
    <path d="M3.71 59.55L18.51 69.6V59.55H3.71Z" fill="url(#trophyGradient)" />
    <path d="M0.09 46.39L11.55 59.7V46.39H0.09Z" fill="url(#trophyGradient)" />
    <path d="M0 32.63L7.52 48.2V32.63H0Z" fill="url(#trophyGradient)" />
    <path d="M2.2 19.44L6.65 36.21V19.44H2.2Z" fill="url(#trophyGradient)" />
    <path d="M6.61 8.54L11.66 24.76V8.54H6.61Z" fill="url(#trophyGradient)" />
    <path d="M13.33 0L19.87 14.71V0H13.33Z" fill="url(#trophyGradient)" />
  </svg>
);

// Chevron Down SVG
const ChevronDownSVG = ({ className = "w-6 h-6" }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4 8L12 16L20 8"
      stroke="currentColor"
      strokeWidth="2"
      fill="none"
    />
  </svg>
);

// Chevron Up SVG
const ChevronUpSVG = ({ className = "w-6 h-6" }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M20 16L12 8L4 16"
      stroke="currentColor"
      strokeWidth="2"
      fill="none"
    />
  </svg>
);

const Drawing = () => {
  const { city } = useParams();
  const navigate = useNavigate();
  const { size } = useWindowSize();
  const {
    setCity,
    setIdInView,
    setOpenPopUp: toglePopUp,
  } = usePageContextFunction();
  const { idInView } = usePageContextState();
  const isMobile = (size?.width || 0) <= 768;

  // State for expandable section
  const [isExpanded, setIsExpanded] = useState(false);
  // State for floating icon
  const [isShowFloating, setIsShowFloating] = useState(true);
  const [navbarShow, setNavbarShow] = useState(false);

  // State to track which images have failed to load
  const [failedImages, setFailedImages] = useState(new Set());

  // Fetch drawing data from API
  const { data: drawingData, isLoading, error } = useFetchDrawingSoc();

  // Memoized function to check if image exists and can be loaded
  const checkImageExists = useCallback((imageUrl, teamId, teamType) => {
    const imageKey = `${teamId}-${teamType}`;

    const img = new Image();
    img.onload = () => {
      setFailedImages((prev) => {
        if (prev.has(imageKey)) {
          const newSet = new Set(prev);
          newSet.delete(imageKey);
          return newSet;
        }
        return prev;
      });
    };
    img.onerror = () => {
      setFailedImages((prev) => {
        if (!prev.has(imageKey)) {
          const newSet = new Set(prev);
          newSet.add(imageKey);
          return newSet;
        }
        return prev;
      });
    };
    img.src = imageUrl;
  }, []);

  useEffect(() => {
    setCity("soc");
    setIdInView("drawing");
  }, []);

  // Listen to navbar clicked
  useEffect(() => {
    window.addEventListener("click", handleClickNavbar);

    return () => {
      window.removeEventListener("click", handleClickNavbar);
    };
  }, []);

  // Floating icon logic
  const isShowFloat = useMemo(() => {
    return (
      isShowFloating && !idInView?.includes("footer-landing") && !navbarShow
    );
  }, [navbarShow, isShowFloating, idInView]);

  const handleHideFloatingIcon = () => {
    setIsShowFloating(false);
  };

  const handleClickNavbar = (e) => {
    const className = String(e.target.className);

    if (className?.includes("navbar")) {
      setNavbarShow(className?.includes("onOpen"));
      handleHideFloatingIcon();
    }
  };

  // Tournament data based on Figma - 3 columns (fallback data)
  const matchesColumn1 = [
    {
      id: 1,
      team1: { name: "-", seed: "1" },
      team2: { name: "-", seed: "2" },
      isPlayed: true,
    },
    {
      id: 2,
      team1: { name: "-", seed: "7" },
      team2: { name: "-", seed: "8" },
      isPlayed: false,
    },
    {
      id: 3,
      team1: { name: "-", seed: "13" },
      team2: { name: "-", seed: "14" },
      isPlayed: false,
    },
    {
      id: 4,
      team1: { name: "-", seed: "19" },
      team2: { name: "-", seed: "20" },
      isPlayed: false,
    },
    {
      id: 5,
      team1: { name: "-", seed: "25" },
      team2: { name: "-", seed: "26" },
      isPlayed: false,
    },
  ];

  const matchesColumn2 = [
    {
      id: 6,
      team1: { name: "-", seed: "3" },
      team2: { name: "-", seed: "4" },
      isPlayed: false,
    },
    {
      id: 7,
      team1: { name: "-", seed: "9" },
      team2: { name: "-", seed: "10" },
      isPlayed: false,
    },
    {
      id: 8,
      team1: { name: "-", seed: "15" },
      team2: { name: "-", seed: "16" },
      isPlayed: false,
    },
    {
      id: 9,
      team1: { name: "-", seed: "21" },
      team2: { name: "-", seed: "22" },
      isPlayed: false,
    },
    {
      id: 10,
      team1: { name: "-", seed: "27" },
      team2: { name: "-", seed: "1" },
      isPlayed: false,
    },
  ];

  const matchesColumn3 = [
    {
      id: 11,
      team1: { name: "-", seed: "5" },
      team2: { name: "-", seed: "6" },
      isPlayed: false,
    },
    {
      id: 12,
      team1: { name: "-", seed: "11" },
      team2: { name: "-", seed: "12" },
      isPlayed: false,
    },
    {
      id: 13,
      team1: { name: "-", seed: "17" },
      team2: { name: "-", seed: "18" },
      isPlayed: false,
    },
    {
      id: 14,
      team1: { name: "-", seed: "23" },
      team2: { name: "-", seed: "24" },
      isPlayed: false,
    },
  ];

  // Additional matches for expanded view
  const expandedMatchesColumn1 = [
    ...matchesColumn1,
    {
      id: 15,
      team1: { name: "-", seed: "4" },
      team2: { name: "-", seed: "5" },
      isPlayed: false,
    },
    {
      id: 16,
      team1: { name: "-", seed: "10" },
      team2: { name: "-", seed: "11" },
      isPlayed: false,
    },
    {
      id: 17,
      team1: { name: "-", seed: "16" },
      team2: { name: "-", seed: "17" },
      isPlayed: false,
    },
    {
      id: 18,
      team1: { name: "-", seed: "22" },
      team2: { name: "-", seed: "23" },
      isPlayed: false,
    },
  ];

  const expandedMatchesColumn2 = [
    ...matchesColumn2,
    {
      id: 19,
      team1: { name: "-", seed: "6" },
      team2: { name: "-", seed: "7" },
      isPlayed: false,
    },
    {
      id: 20,
      team1: { name: "-", seed: "12" },
      team2: { name: "-", seed: "13" },
      isPlayed: false,
    },
    {
      id: 21,
      team1: { name: "-", seed: "18" },
      team2: { name: "-", seed: "19" },
      isPlayed: false,
    },
    {
      id: 22,
      team1: { name: "-", seed: "24" },
      team2: { name: "-", seed: "25" },
      isPlayed: false,
    },
  ];

  const expandedMatchesColumn3 = [
    ...matchesColumn3,
    {
      id: 23,
      team1: { name: "-", seed: "2" },
      team2: { name: "-", seed: "3" },
      isPlayed: false,
    },
    {
      id: 24,
      team1: { name: "-", seed: "8" },
      team2: { name: "-", seed: "9" },
      isPlayed: false,
    },
    {
      id: 25,
      team1: { name: "-", seed: "14" },
      team2: { name: "-", seed: "15" },
      isPlayed: false,
    },
    {
      id: 26,
      team1: { name: "-", seed: "20" },
      team2: { name: "-", seed: "21" },
      isPlayed: false,
    },
    {
      id: 27,
      team1: { name: "-", seed: "26" },
      team2: { name: "-", seed: "27" },
      isPlayed: false,
    },
  ];
  console.log({ drawingData });

  // Process API data or use fallback static data
  const processedData = React.useMemo(() => {
    if (drawingData?.data?.data?.rounds?.length > 0) {
      // Get all matches from all rounds
      const allMatches = [];
      let totalMatchesFromAllRounds = 0;
      let totalValidMatches = 0;

      console.log(
        `Total rounds in API: ${drawingData.data.data.rounds.length}`
      );

      drawingData.data.data.rounds.forEach((round, roundIndex) => {
        if (round.matches && round.matches.length > 0) {
          console.log(
            `Round ${roundIndex + 1} (${round.round_name}): ${
              round.matches.length
            } total matches`
          );
          totalMatchesFromAllRounds += round.matches.length;

          // Add all matches from this round (no filtering)
          allMatches.push(...round.matches);
        }
      });

      console.log(
        `TOTAL MATCHES FROM ALL ROUNDS: ${totalMatchesFromAllRounds}`
      );

      // Transform API matches to match our expected structure
      const transformedMatches = allMatches.map((match) => ({
        id: match.match_number,
        team1: {
          name: match.team1?.school_name || "-",
          seed: match.team1_number?.toString() || "",
          hasLogo: !!match.team1?.school_logo,
          school_logo: match.team1?.school_logo,
        },
        team2: {
          name: match.team2?.school_name || "-",
          seed: match.team2_number?.toString() || "",
          hasLogo: !!match.team2?.school_logo,
          school_logo: match.team2?.school_logo,
        },
        isPlayed: match.is_scheduled,
        roundNumber: match.round_number,
      }));

      console.log(`TOTAL MATCH CARDS FROM API: ${transformedMatches.length}`);
      console.log(`First few API matches:`, transformedMatches.slice(0, 3));
      console.log(`Last few API matches:`, transformedMatches.slice(-3));

      // Create a map of existing matches by their ID for quick lookup
      const matchesMap = {};
      transformedMatches.forEach((match) => {
        matchesMap[match.id] = match;
      });

      // Generate 14 initial matches, using API data where available, placeholder otherwise
      const initialMatches = [];
      for (let i = 1; i <= 14; i++) {
        if (matchesMap[i]) {
          // Use API data if available
          initialMatches.push(matchesMap[i]);
        } else {
          // Create placeholder match
          initialMatches.push({
            id: i,
            team1: {
              name: "-",
              seed: (i * 2 - 1).toString(), // Sequential seeds: 1, 3, 5, 7, etc.
              hasLogo: false,
              school_logo: null,
            },
            team2: {
              name: "-",
              seed: (i * 2).toString(), // Sequential seeds: 2, 4, 6, 8, etc.
              hasLogo: false,
              school_logo: null,
            },
            isPlayed: false,
            roundNumber: 1,
          });
        }
      }

      // Generate 27 expanded matches for full tournament
      const allExpandedMatches = [];
      for (let i = 1; i <= 27; i++) {
        if (matchesMap[i]) {
          // Use API data if available
          allExpandedMatches.push(matchesMap[i]);
        } else {
          // Create placeholder match
          allExpandedMatches.push({
            id: i,
            team1: {
              name: "-",
              seed: (i * 2 - 1).toString(), // Sequential seeds: 1, 3, 5, 7, etc.
              hasLogo: false,
              school_logo: null,
            },
            team2: {
              name: "-",
              seed: (i * 2).toString(), // Sequential seeds: 2, 4, 6, 8, etc.
              hasLogo: false,
              school_logo: null,
            },
            isPlayed: false,
            roundNumber: 1,
          });
        }
      }

      // Desktop: Distribute matches column-wise (filling columns first)
      // Mobile: Should display row-wise (row 1: items 1,2,3; row 2: items 4,5,6; etc.)
      const itemsPerRow = 3;

      // For desktop - distribute column-wise
      const initialColumn1 = [];
      const initialColumn2 = [];
      const initialColumn3 = [];

      for (let i = 0; i < initialMatches.length; i++) {
        const columnIndex = i % itemsPerRow;
        if (columnIndex === 0) initialColumn1.push(initialMatches[i]);
        else if (columnIndex === 1) initialColumn2.push(initialMatches[i]);
        else initialColumn3.push(initialMatches[i]);
      }

      // For mobile - create row-wise ordering
      const mobileInitialMatches = initialMatches; // Mobile shows all matches in single column, row by row

      // For expanded view, show all matches distributed across columns (desktop)
      const expandedColumn1 = [];
      const expandedColumn2 = [];
      const expandedColumn3 = [];

      for (let i = 0; i < allExpandedMatches.length; i++) {
        const columnIndex = i % itemsPerRow;
        if (columnIndex === 0) expandedColumn1.push(allExpandedMatches[i]);
        else if (columnIndex === 1) expandedColumn2.push(allExpandedMatches[i]);
        else expandedColumn3.push(allExpandedMatches[i]);
      }

      // For mobile expanded view
      const mobileExpandedMatches = allExpandedMatches; // Mobile shows all matches in order

      return {
        matchesColumn1: initialColumn1,
        matchesColumn2: initialColumn2,
        matchesColumn3: initialColumn3,
        expandedMatchesColumn1: expandedColumn1,
        expandedMatchesColumn2: expandedColumn2,
        expandedMatchesColumn3: expandedColumn3,
        // Mobile data
        mobileInitialMatches,
        mobileExpandedMatches,
      };
    }

    // Fallback to static data if API hasn't loaded or failed
    return {
      matchesColumn1,
      matchesColumn2,
      matchesColumn3,
      expandedMatchesColumn1,
      expandedMatchesColumn2,
      expandedMatchesColumn3,
      // Mobile fallback data - create sequential order for mobile
      mobileInitialMatches: [
        {
          id: 1,
          team1: { name: "-", seed: "1" },
          team2: { name: "-", seed: "2" },
          isPlayed: true,
        },
        {
          id: 2,
          team1: { name: "-", seed: "3" },
          team2: { name: "-", seed: "4" },
          isPlayed: false,
        },
        {
          id: 3,
          team1: { name: "-", seed: "5" },
          team2: { name: "-", seed: "6" },
          isPlayed: false,
        },
        {
          id: 4,
          team1: { name: "-", seed: "7" },
          team2: { name: "-", seed: "8" },
          isPlayed: false,
        },
        {
          id: 5,
          team1: { name: "-", seed: "9" },
          team2: { name: "-", seed: "10" },
          isPlayed: false,
        },
        {
          id: 6,
          team1: { name: "-", seed: "11" },
          team2: { name: "-", seed: "12" },
          isPlayed: false,
        },
        {
          id: 7,
          team1: { name: "-", seed: "13" },
          team2: { name: "-", seed: "14" },
          isPlayed: false,
        },
        {
          id: 8,
          team1: { name: "-", seed: "15" },
          team2: { name: "-", seed: "16" },
          isPlayed: false,
        },
        {
          id: 9,
          team1: { name: "-", seed: "17" },
          team2: { name: "-", seed: "18" },
          isPlayed: false,
        },
        {
          id: 10,
          team1: { name: "-", seed: "19" },
          team2: { name: "-", seed: "20" },
          isPlayed: false,
        },
        {
          id: 11,
          team1: { name: "-", seed: "21" },
          team2: { name: "-", seed: "22" },
          isPlayed: false,
        },
        {
          id: 12,
          team1: { name: "-", seed: "23" },
          team2: { name: "-", seed: "24" },
          isPlayed: false,
        },
        {
          id: 13,
          team1: { name: "-", seed: "25" },
          team2: { name: "-", seed: "26" },
          isPlayed: false,
        },
        {
          id: 14,
          team1: { name: "-", seed: "27" },
          team2: { name: "-", seed: "28" },
          isPlayed: false,
        },
      ],
      mobileExpandedMatches: [
        {
          id: 1,
          team1: { name: "-", seed: "1" },
          team2: { name: "-", seed: "2" },
          isPlayed: true,
        },
        {
          id: 2,
          team1: { name: "-", seed: "3" },
          team2: { name: "-", seed: "4" },
          isPlayed: false,
        },
        {
          id: 3,
          team1: { name: "-", seed: "5" },
          team2: { name: "-", seed: "6" },
          isPlayed: false,
        },
        {
          id: 4,
          team1: { name: "-", seed: "7" },
          team2: { name: "-", seed: "8" },
          isPlayed: false,
        },
        {
          id: 5,
          team1: { name: "-", seed: "9" },
          team2: { name: "-", seed: "10" },
          isPlayed: false,
        },
        {
          id: 6,
          team1: { name: "-", seed: "11" },
          team2: { name: "-", seed: "12" },
          isPlayed: false,
        },
        {
          id: 7,
          team1: { name: "-", seed: "13" },
          team2: { name: "-", seed: "14" },
          isPlayed: false,
        },
        {
          id: 8,
          team1: { name: "-", seed: "15" },
          team2: { name: "-", seed: "16" },
          isPlayed: false,
        },
        {
          id: 9,
          team1: { name: "-", seed: "17" },
          team2: { name: "-", seed: "18" },
          isPlayed: false,
        },
        {
          id: 10,
          team1: { name: "-", seed: "19" },
          team2: { name: "-", seed: "20" },
          isPlayed: false,
        },
        {
          id: 11,
          team1: { name: "-", seed: "21" },
          team2: { name: "-", seed: "22" },
          isPlayed: false,
        },
        {
          id: 12,
          team1: { name: "-", seed: "23" },
          team2: { name: "-", seed: "24" },
          isPlayed: false,
        },
        {
          id: 13,
          team1: { name: "-", seed: "25" },
          team2: { name: "-", seed: "26" },
          isPlayed: false,
        },
        {
          id: 14,
          team1: { name: "-", seed: "27" },
          team2: { name: "-", seed: "28" },
          isPlayed: false,
        },
        {
          id: 15,
          team1: { name: "-", seed: "29" },
          team2: { name: "-", seed: "30" },
          isPlayed: false,
        },
        {
          id: 16,
          team1: { name: "-", seed: "31" },
          team2: { name: "-", seed: "32" },
          isPlayed: false,
        },
        {
          id: 17,
          team1: { name: "-", seed: "33" },
          team2: { name: "-", seed: "34" },
          isPlayed: false,
        },
        {
          id: 18,
          team1: { name: "-", seed: "35" },
          team2: { name: "-", seed: "36" },
          isPlayed: false,
        },
        {
          id: 19,
          team1: { name: "-", seed: "37" },
          team2: { name: "-", seed: "38" },
          isPlayed: false,
        },
        {
          id: 20,
          team1: { name: "-", seed: "39" },
          team2: { name: "-", seed: "40" },
          isPlayed: false,
        },
        {
          id: 21,
          team1: { name: "-", seed: "41" },
          team2: { name: "-", seed: "42" },
          isPlayed: false,
        },
        {
          id: 22,
          team1: { name: "-", seed: "43" },
          team2: { name: "-", seed: "44" },
          isPlayed: false,
        },
        {
          id: 23,
          team1: { name: "-", seed: "45" },
          team2: { name: "-", seed: "46" },
          isPlayed: false,
        },
        {
          id: 24,
          team1: { name: "-", seed: "47" },
          team2: { name: "-", seed: "48" },
          isPlayed: false,
        },
        {
          id: 25,
          team1: { name: "-", seed: "49" },
          team2: { name: "-", seed: "50" },
          isPlayed: false,
        },
        {
          id: 26,
          team1: { name: "-", seed: "51" },
          team2: { name: "-", seed: "52" },
          isPlayed: false,
        },
        {
          id: 27,
          team1: { name: "-", seed: "53" },
          team2: { name: "-", seed: "54" },
          isPlayed: false,
        },
      ],
    };
  }, [drawingData]);

  const MatchCard = React.memo(({ match }) => {
    const team1ImageKey = `${match.id}-team1`;
    const team2ImageKey = `${match.id}-team2`;

    const team1ImageFailed = failedImages.has(team1ImageKey);
    const team2ImageFailed = failedImages.has(team2ImageKey);

    // Only check images once when component mounts or when image URLs change
    useEffect(() => {
      const checkedImages = new Set();

      if (
        match.team1.hasLogo &&
        match.team1.school_logo &&
        !checkedImages.has(team1ImageKey)
      ) {
        checkedImages.add(team1ImageKey);
        checkImageExists(imgPath + match.team1.school_logo, match.id, "team1");
      }
      if (
        match.team2.hasLogo &&
        match.team2.school_logo &&
        !checkedImages.has(team2ImageKey)
      ) {
        checkedImages.add(team2ImageKey);
        checkImageExists(imgPath + match.team2.school_logo, match.id, "team2");
      }
    }, [
      match.id,
      match.team1.school_logo,
      match.team2.school_logo,
      checkImageExists,
    ]);

    return (
      <div
        className="relative"
        style={{
          background: `linear-gradient(270deg, rgba(49, 67, 88, 0.00) 67.02%, #C97D33 100%), rgba(0, 0, 0, 0.35)`,
          border: "1px solid",
          borderImage: "linear-gradient(180deg, #F2B61A 0%, #C97D33 100%) 1",
          borderRadius: "7px",
          padding: "8px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "14px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: "4px",
            borderRadius: "8px",
          }}
        >
          {/* Team 1 Row */}
          <div
            className="relative"
            style={{
              display: "flex",
              justifyContent: "stretch",
              alignItems: "stretch",
              width: isMobile ? "320px" : "376px",
              padding: "8px 12px",
              // gap: "24px",
              borderRadius: "7px",
            }}
          >
            {/* Seed Badge - positioned at left edge */}
            <div
              style={{
                backgroundColor: "#FFFFFF",
                borderRadius: "54px",
                padding: "4px 3px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minWidth: "27px",
              }}
            >
              <span
                className="font-bold"
                style={{
                  fontSize: "18px",
                  lineHeight: "1.3333em",
                  letterSpacing: "0.5%",
                  textAlign: "center",
                  color: "#1F2D3D",
                  width: "27px",
                }}
              >
                {match.team1.seed || ""}
              </span>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "8px",
                flex: 1,
              }}
            >
              {match.team1.hasLogo &&
                match.team1.school_logo &&
                !team1ImageFailed && (
                  <div
                    style={{
                      width: "24px",
                      height: "24px",

                      backgroundImage: `url("${
                        imgPath + match.team1.school_logo
                      }")`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                  />
                )}
              <span
                className="font-bold"
                style={{
                  fontSize: "18px",
                  lineHeight: "1.3333em",
                  letterSpacing: "0.5%",
                  textAlign: "center",
                  color: "#FFFFFF",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: isMobile ? "210px" : "260px",
                }}
              >
                {/* test */}
                {match.team1.name}
              </span>
            </div>
          </div>

          {/* VS */}
          <div className="ml-12 flex justify-center items-center">
            <div
              className="font-bold"
              style={{
                fontSize: "16px",
                lineHeight: "1.0714em",
                textTransform: "uppercase",
                background: "linear-gradient(180deg, #F2B61A 0%, #C97D33 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              VS
            </div>
          </div>

          {/* Team 2 Row */}
          <div
            className="relative"
            style={{
              display: "flex",
              justifyContent: "stretch",
              alignItems: "stretch",
              width: isMobile ? "320px" : "376px",
              padding: "8px 12px",
              // gap: "24px",
              borderRadius: "7px",
            }}
          >
            {/* Seed Badge - positioned at left edge */}
            <div
              style={{
                backgroundColor: "#FFFFFF",
                borderRadius: "54px",
                padding: "4px 3px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minWidth: "27px",
              }}
            >
              <span
                className="font-bold"
                style={{
                  fontSize: "18px",
                  lineHeight: "1.3333em",
                  letterSpacing: "0.5%",
                  textAlign: "center",
                  color: "#1F2D3D",
                  width: "27px",
                }}
              >
                {match.team2.seed || ""}
              </span>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "8px",
                flex: 1,
              }}
            >
              {match.team2.hasLogo &&
                match.team2.school_logo &&
                !team2ImageFailed && (
                  <div
                    style={{
                      width: "24px",
                      height: "24px",

                      backgroundImage: `url("${
                        imgPath + match.team2.school_logo
                      }")`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                  />
                )}
              <span
                className="font-bold"
                style={{
                  fontSize: "18px",
                  lineHeight: "1.3333em",
                  letterSpacing: "0.5%",
                  textAlign: "center",
                  color: "#FFFFFF",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: isMobile ? "210px" : "260px",
                }}
              >
                {match.team2.name}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  });

  const ExpandToggleCard = () => (
    <div
      onClick={() => {
        // Scroll to top of drawing section when collapsing.
        if (isExpanded) {
          func.scrollTo("jadwal-pertandingan", "", 800, "auto");
        }
        setIsExpanded(!isExpanded);
      }}
      className="relative flex justify-center items-center cursor-pointer"
      style={{
        background: `linear-gradient(270deg, rgba(49, 67, 88, 0.00) 67.02%, #C97D33 100%), rgba(0, 0, 0, 0.35)`,
        border: "1px solid",
        borderImage: "linear-gradient(180deg, #F2B61A 0%, #C97D33 100%) 1",
        borderRadius: "7px",
        padding: "8px",
        width: isMobile ? "336px" : "392px", // Match MatchCard width (320px content + 16px padding for mobile, 376px + 16px for desktop)
        minHeight: 137,
      }}
    >
      <button className="flex items-center gap-[14px] text-white font-normal text-lg hover:text-yellow-400 transition-colors">
        <span className="text-[18px] font-bold">
          {isExpanded ? "Sembunyikan" : "Lihat Selengkapnya"}
        </span>
        {isExpanded ? (
          <ChevronUpSVG className="w-6 h-6" />
        ) : (
          <ChevronDownSVG className="w-6 h-6" />
        )}
      </button>
    </div>
  );

  return (
    <div style={{ margin: 0, padding: 0, minHeight: "100vh" }}>
      <Section
        id="drawing"
        height={isExpanded ? "auto" : "auto"}
        background={"transparent"}
        padding={"0"}
        margin={"0"}
        style={{ marginBottom: 0, flex: isExpanded ? "0 0 auto" : "1" }}
      >
        <div
          className="w-full"
          style={{
            minHeight: isExpanded ? "auto" : "100vh",
            background: `url(${func.getStaticImg(
              `${
                isMobile ? "/bg/bg-talent-m.png" : "/bg/bg-full-presscon-d.jpg"
              }`
            )}) no-repeat bottom / cover`,
            paddingBottom: isMobile ? "7vh" : "0",
          }}
        >
          <div
            className="z-10 px-4 md:px-[260px] py-8 pt-52 flex flex-col"
            style={{ minHeight: isExpanded ? "auto" : "100vh" }}
          >
            {/* Header with Trophies */}
            <div className="flex justify-center items-center gap-2">
              <img
                src={func.getStaticImg("/icon/left-trophy-leaf.svg")}
                className="w-[42px] h-[84px]"
              />
              <h1
                className="text-white text-[40px] font-bold text-center leading-[1.25em] tracking-[0.25%]"
                style={{
                  textShadow:
                    "0px 7px 8px rgba(0, 0, 0, 0.05), 0px 5px 22px 4px rgba(0, 0, 0, 0.05), 0px 12px 17px 2px rgba(0, 0, 0, 0.05)",
                }}
              >
                Stage of Champions Drawing
              </h1>
              <img
                src={func.getStaticImg("/icon/right-trophy-leaf.svg")}
                className="w-[42px] h-[84px]"
              />
            </div>

            {/* Tournament Bracket Container */}
            <div className="flex-1 p-6 !pt-24 md:p-8 max-w-none mx-auto mb-0 md:mb-8 flex  justify-center">
              {isLoading ? (
                <div className="flex justify-center items-center">
                  {/* <div className="text-white text-lg">
                    Loading drawing data...
                  </div> */}
                  <Loading />
                </div>
              ) : error ? (
                <div className="flex justify-center items-center">
                  <div className="text-red-400 text-lg">
                    Error loading drawing data
                  </div>
                </div>
              ) : (
                /* Matches Grid - 3 Columns */
                <div
                  className={`flex ${
                    isMobile
                      ? "flex-col items-center"
                      : "flex-row justify-center"
                  } gap-3`}
                >
                  {isMobile ? (
                    /* Mobile Layout - Single Column with Row Order */
                    <div className="flex flex-col gap-4 items-center">
                      {/* Initial matches for mobile */}
                      {(isExpanded
                        ? processedData.mobileExpandedMatches
                        : processedData.mobileInitialMatches
                      ).map((match) => (
                        <MatchCard key={match.id} match={match} />
                      ))}
                    </div>
                  ) : (
                    /* Desktop Layout - 3 Columns */
                    <>
                      {/* Column 1 */}
                      <div className="flex flex-col gap-4 items-center">
                        {processedData.matchesColumn1.map((match) => (
                          <MatchCard key={match.id} match={match} />
                        ))}

                        {/* Additional matches with transition */}
                        <div
                          className={`transition-all duration-500 ease-in-out ${
                            isExpanded
                              ? "opacity-100 translate-y-0"
                              : "opacity-0 -translate-y-4 pointer-events-none"
                          }`}
                        >
                          {isExpanded && (
                            <div className="flex flex-col gap-4 items-center">
                              {processedData.expandedMatchesColumn1
                                .slice(processedData.matchesColumn1.length)
                                .map((match) => (
                                  <MatchCard key={match.id} match={match} />
                                ))}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Column 2 */}
                      <div className="flex flex-col gap-4 items-center">
                        {processedData.matchesColumn2.map((match) => (
                          <MatchCard key={match.id} match={match} />
                        ))}

                        {/* Additional matches with transition */}
                        <div
                          className={`transition-all duration-500 ease-in-out ${
                            isExpanded
                              ? "opacity-100 translate-y-0"
                              : "opacity-0 -translate-y-4 pointer-events-none"
                          }`}
                        >
                          {isExpanded && (
                            <div className="flex flex-col gap-4 items-center">
                              {processedData.expandedMatchesColumn2
                                .slice(processedData.matchesColumn2.length)
                                .map((match) => (
                                  <MatchCard key={match.id} match={match} />
                                ))}
                            </div>
                          )}
                        </div>

                        {/* Show Expand/Collapse Toggle in center column when expanded */}
                        {isExpanded && <ExpandToggleCard />}
                      </div>

                      {/* Column 3 */}
                      <div className="flex flex-col gap-4 items-center">
                        {(isExpanded
                          ? processedData.expandedMatchesColumn3
                          : processedData.matchesColumn3
                        ).map((match) => (
                          <MatchCard key={match.id} match={match} />
                        ))}

                        {/* Show Expand/Collapse Toggle in right column when collapsed */}
                        {!isExpanded && <ExpandToggleCard />}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Mobile: Show expand toggle after matches */}
            {isMobile && (
              <div className="flex justify-center mb-8">
                <ExpandToggleCard />
              </div>
            )}

            {/* CTA Button */}
            <div
              className="flex justify-center mt-16 mb-16"
              id="jadwal-pertandingan"
            >
              <Button
                variant={"secondary-white"}
                size={"small"}
                className={
                  isMobile ? "w-[327px] h-[44px]" : "w-[392px] h-[44px]"
                }
                onClick={() => navigate("/soc/jadwal-pertandingan")}
              >
                Lihat Jadwal Pertandingan
              </Button>
            </div>
          </div>
        </div>
      </Section>

      {/* Footer Section */}
      <Section
        id={"footer-landing"}
        backgroundColor={"#011828"}
        //  scrollSnap={"end"}
        height={"auto"}
        padding={"0"}
        margin={"0"}
        style={{ marginTop: 0, flexShrink: 0 }}
      >
        <NewFooter />
      </Section>

      {/* Floating Icon */}
      {isShowFloat && (
        <FloatingIconWrapper
          key={isShowFloat}
          style={isShowFloat ? styles.onShow : styles.onHide}
          onClick={() => {
            setIdInView("banner");
            func.scrollTo("footer-landing");
          }}
        >
          <Lottie
            style={{ width: 120, height: 120 }}
            options={{
              animationData: animationData,
            }}
          />
        </FloatingIconWrapper>
      )}
    </div>
  );
};

const styles = {
  onHide: {
    opacity: 0,
  },
  onShow: {
    opacity: 1,
    transition: "opacity 0.2s linear",
  },
};

export default Drawing;
